import {
  addDoc,
  doc,
  updateDoc,
  writeBatch,
} from 'firebase/firestore';
import { checkInDatesCollectionRef, db } from '../firebase';
import {
  AuditActor,
  asFirestoreData,
  logActivity,
  stripSentinels,
} from '../audit-log';
import { diffFields } from '../diff-fields';
import type { CheckinDate } from '../types';

const checkInDateLabel = (d: CheckinDate): string =>
  d.date.toDate().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

export const createCheckInDate = async (
  actor: AuditActor | null,
  payload: Record<string, unknown>,
): Promise<string> => {
  const docRef = await addDoc(checkInDatesCollectionRef, payload);
  const snapshot = stripSentinels(payload);
  const dateField = snapshot.date as { toDate(): Date } | undefined;
  const entityLabel = dateField
    ? dateField.toDate().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : '(date)';
  logActivity({
    actor,
    entity: 'check-in-date',
    entityRef: docRef.id,
    entityLabel,
    action: 'create',
    eventRef: snapshot.eventRef as string | undefined,
    snapshot,
  });
  return docRef.id;
};

export const updateCheckInDate = async (
  actor: AuditActor | null,
  before: CheckinDate,
  payload: Record<string, unknown>,
): Promise<void> => {
  await updateDoc(
    doc(checkInDatesCollectionRef, before.ref),
    asFirestoreData(payload),
  );
  const changes = diffFields(before, payload);
  if (Object.keys(changes).length === 0) return;
  logActivity({
    actor,
    entity: 'check-in-date',
    entityRef: before.ref,
    entityLabel: checkInDateLabel(before),
    action: 'update',
    eventRef: before.eventRef,
    changes,
  });
};

// Toggles `isActive` on `target` while atomically demoting every other
// active check-in date in the same event (so only one is ever active at
// a time — the data model doesn't enforce this; the batch does).
export const activateCheckInDate = async (
  actor: AuditActor | null,
  target: CheckinDate,
  allDates: CheckinDate[],
): Promise<void> => {
  const others = allDates.filter((d) => d.isActive && d.ref !== target.ref);
  const previousActiveDateRef = others.length > 0 ? others[0].ref : null;

  if (target.isActive) {
    await updateDoc(doc(checkInDatesCollectionRef, target.ref), {
      isActive: false,
    });
    logActivity({
      actor,
      entity: 'check-in-date',
      entityRef: target.ref,
      entityLabel: checkInDateLabel(target),
      action: 'update',
      eventRef: target.eventRef,
      changes: { isActive: { from: true, to: false } },
    });
    return;
  }

  const batch = writeBatch(db);
  others.forEach((d) => {
    batch.update(doc(checkInDatesCollectionRef, d.ref), { isActive: false });
  });
  batch.update(doc(checkInDatesCollectionRef, target.ref), { isActive: true });
  await batch.commit();

  logActivity({
    actor,
    entity: 'check-in-date',
    entityRef: target.ref,
    entityLabel: checkInDateLabel(target),
    action: 'activate',
    eventRef: target.eventRef,
    meta: { previousActiveDateRef },
  });
};
